import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  UnlockLockableInput,
  unlockLockableMutation,
  unlockLockableMutation$data,
  unlockLockableMutation$rawResponse,
} from './__generated__/unlockLockableMutation.graphql'

type IssueMutationProps = {
  environment: Environment
  input: UnlockLockableInput
  onError?: (error: Error) => void
  onCompleted?: (response: unlockLockableMutation$data) => void
}

type RawUnlockedRecord = NonNullable<unlockLockableMutation$rawResponse['unlockLockable']>['unlockedRecord']
type DataUnlockedRecord = NonNullable<unlockLockableMutation$data['unlockLockable']>['unlockedRecord']

function toObjectPropertiesPatch({
  lockableId,
  locked,
}: {
  lockableId: string
  locked: boolean | null | undefined
}): ObjectPropertiesPatch | undefined {
  if (locked === undefined || locked === null) return undefined
  return {id: lockableId, values: {locked}}
}

function getLockedValue(
  unlockedRecord: RawUnlockedRecord | DataUnlockedRecord | null | undefined,
): boolean | undefined {
  if (!unlockedRecord || !('locked' in unlockedRecord)) return undefined
  return unlockedRecord.locked
}

export function commitUnlockLockableMutation({
  environment,
  input: {lockableId},
  onError,
  onCompleted,
}: IssueMutationProps) {
  const inputHash: UnlockLockableInput = {
    lockableId,
  }

  const optimisticResponse: unlockLockableMutation$rawResponse = {
    unlockLockable: {
      unlockedRecord: {
        __typename: 'Issue',
        __isNode: 'Issue',
        id: lockableId,
        locked: false,
      },
    },
  }

  const optimisticPatch = toObjectPropertiesPatch({
    lockableId,
    locked: getLockedValue(optimisticResponse.unlockLockable?.unlockedRecord),
  })
  const optimisticEvent =
    optimisticPatch &&
    new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'optimistic', data: optimisticPatch})
  const mutationId = optimisticEvent?.detail.mutationId ?? createMutationId()

  const disposable = commitMutation<unlockLockableMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation unlockLockableMutation($input: UnlockLockableInput!) @raw_response_type {
        unlockLockable(input: $input) {
          unlockedRecord {
            ... on Issue {
              locked
            }
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    optimisticResponse,
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      if (onError) onError(error)
    },
    onCompleted: response => {
      const patch = toObjectPropertiesPatch({
        lockableId,
        locked: getLockedValue(response.unlockLockable?.unlockedRecord),
      })
      if (patch) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
      }
      if (onCompleted) onCompleted(response)
    },
  })

  if (optimisticEvent) {
    dispatchMutationLifecycleEvent(optimisticEvent)
  }

  return disposable
}
